import { PropsWithChildren, useEffect, useRef } from 'react';

import { createInstance, Identify } from '@amplitude/analytics-browser';
import { BrowserOptions } from '@amplitude/analytics-types';
import get from 'lodash/get';
import { z } from 'zod';

import useAuth from '@/hooks/useAuth';

import { useFeaturePermission } from '@/utils/permissions';
import { getHighestUserPermission } from '@/utils/user';

import { OktaUserRaw } from '@/types';

import Constants from 'constants/index';

import { addPlatformPlugin } from './AmplitudePlatformPlugin';
import { addUrlNormalizationPlugin } from './AmplitudeUrlPlugin';
import { AmplitudeContext, Track } from './useAmplitude';

const nativeAmplitudeIdsSchema = z.object({
  deviceId: z.string().min(1),
  sessionId: z.number(),
});

interface AmplitudeProviderProps extends PropsWithChildren {
  user?: OktaUserRaw;
  options?: BrowserOptions;
}

export const AmplitudeProvider: React.FC<AmplitudeProviderProps> = ({
  user,
  options,
  children,
}) => {
  const analyticsEnabled = useFeaturePermission('analytics');

  const amplitudeRef = useRef<ReturnType<typeof createInstance> | null>(null);
  const metaRef = useRef<{
    permissionLevel: string | undefined;
    userType: unknown;
    userPerms: unknown;
    userLocations: unknown;
  } | null>(null);

  const { data: auth } = useAuth({ enabled: !!user });

  useEffect(() => {
    if (user) {
      const permissionLevel = getHighestUserPermission(
        get(user.cfa_perms, Constants.APPLICATION_SYSTEM_TAG),
      );
      metaRef.current = {
        permissionLevel,
        userType: user.userType,
        userPerms: user.cfa_perms,
        userLocations: user['cfa-locations'],
      };
    }

    const apiKey = Constants.AMPLITUDE_API_KEY;
    if (analyticsEnabled && apiKey && options && user && auth) {
      if (!amplitudeRef.current) {
        const amplitudeInstance = createInstance();
        amplitudeInstance.add(addPlatformPlugin());
        amplitudeInstance.add(addUrlNormalizationPlugin());

        const nativeAmplitudeIds = window.native.isNative
          ? nativeAmplitudeIdsSchema.safeParse({
              deviceId: window.amplitudeDeviceId,
              sessionId: window.amplitudeSessionId,
            })
          : undefined;
        const nativeAmplitudeOptions: BrowserOptions | undefined =
          nativeAmplitudeIds?.success
            ? {
                ...nativeAmplitudeIds.data,
                defaultTracking: {
                  sessions: false,
                  pageViews: true,
                  formInteractions: false,
                  fileDownloads: false,
                  attribution: false,
                },
              }
            : undefined;

        amplitudeInstance.init(apiKey, user['cfa-guid'], {
          ...options,
          ...nativeAmplitudeOptions,
        });

        amplitudeRef.current = amplitudeInstance;
      }

      const permissionLevel = getHighestUserPermission(
        get(user.cfa_perms, Constants.APPLICATION_SYSTEM_TAG),
      );
      const identifyObj = new Identify();
      identifyObj.set('user_locations', user['cfa-locations']);
      identifyObj.set('user_type', user.userType);
      identifyObj.set('permission_level', permissionLevel);
      identifyObj.set('is_webview', window.native.isNative);
      identifyObj.set('user_country', auth.country.id);
      identifyObj.set('user_audience', auth.audience);
      const userState =
        auth.state && auth.state.length > 0 ? auth.state.join(',') : 'N/A';
      identifyObj.set('user_state', userState);
      amplitudeRef.current.identify(identifyObj);
    }
  }, [analyticsEnabled, options, user, auth]);

  const track: Track = (eventName: string, properties: object) => {
    const instance = amplitudeRef.current;
    if (!instance) return;
    const date = new Date().toLocaleDateString();
    const time = new Date().toLocaleTimeString();
    const version = Constants.APP_VERSION;
    const env = Constants.ENV;
    instance.track('UserAction', {
      action: eventName,
      date,
      permission_level: metaRef.current?.permissionLevel,
      time,
      version,
      user_type: metaRef.current?.userType,
      user_perms: metaRef.current?.userPerms,
      user_locations: metaRef.current?.userLocations,
      env,
      ...properties,
    });
  };

  return (
    <AmplitudeContext.Provider value={track}>
      {children}
    </AmplitudeContext.Provider>
  );
};
